import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { Adapt } from './lib/adapt.js';
import { SceneClock } from './lib/scene-clock.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { loadArtworkTexture } from '../artwork-texture.js';
import { vdjColor, vdjBeatChroma, vdjCameraKick, VdjPost, VdjComfort, VDJ_PRESETS, VDJ_DROP_FLASH } from './lib/vdjLook.js';

// Contrat du rig partagé : interdit. Un quad plein écran, tout en 2D.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'datamosh',
  family: 'shader',
  danceability: 0.90,
  energyLevel: 0.88,
  bpmMin: 80,
  bpmMax: 175,
  movement: 0.90,
  tonality: 0.40,
  enabled: true
};

// Datamosh : le glitch du datamoshing. Quand une vidéo perd ses images clés,
// les pixels de l'image précédente continuent d'être déplacés par les
// vecteurs de mouvement de la nouvelle : l'image coule, se déchire en blocs,
// se fond dans la suivante. Ici une source vidéo procédurale très colorée
// est « compressée » et cassée en rythme. Bass music, techno, hip-hop, trap,
// électro, hyperpop. Règles R1-R20 de kiosk-app/CLAUDE.md, preset party de
// lib/vdjLook.js.
//
// LA SOURCE (cible 640 x 360, tier 0 : 480 x 270) : cinq scènes lisibles et
// saturées : la pochette plein cadre qui zoome (R18 : échelle max, aucun
// bord), la pochette en grille qui défile, des formes néon qui tournent, le
// titre en capitales géantes (rasterisé une fois par morceau, R12), un
// tunnel de couleurs. Une scène par mesure, une famille (ordre et palette)
// par phrase.
//
// LE CODEC SIMULÉ : macroblocs de 16 x 16 px. Un champ de mouvement PAR BLOC
// (texture 40 x 23, un texel par bloc, lecture au plus proche) : les vecteurs
// CONNUS de la scène courante (zoom, rotation, défilement : la source est
// procédurale, son mouvement est analytique) plus les vecteurs de glitch
// pilotés par la musique (rangées de blocs qui glissent horizontalement sur
// le kick, tourbillon sur les aigus, explosion radiale après le drop).
// L'image affichée est une P-FRAME : l'image précédente déplacée par le
// champ, dans un ping-pong (R15 : les I-frames font office de retour au
// propre) - vecteur constant par bloc (bords de blocs NETS), lecture
// bilinéaire à l'intérieur. Les I-FRAMES (la source propre) recalent tout
// d'un coup. Artefacts : blocs qui figent (copie de l'image d'il y a 4
// images, gardée dans une cible à part), blocs posterisés à 4 niveaux (DCT
// simulée, pas au tier 0), lignes de déchirement horizontales, séparation
// RGB par bloc.
//
// LE RYTHME (party, R9) :
//   TEMPS   I-frame sur les temps 1 et 3 (sauf le temps 1 d'une mesure, qui
//           change de scène SANS I-frame), P-frames entre ; glissement de
//           rangées de blocs sur le kick
//   MESURE  nouvelle scène sans I-frame : les couleurs de la scène précédente
//           coulent dans le mouvement de la nouvelle pendant 2 temps, puis
//           I-frame
//   PHRASE  nouvelle famille de scènes, palette suivante
//   MONTÉE  I-frames de plus en plus rares (probabilité 1 - montée) :
//           l'image fond
//   DROP    I-frame éclatante, flash (VdjComfort), puis une mesure de blocs
//           qui explosent vers l'extérieur
// INTENSITÉ (adapt.js) sous 0,6 : I-frame à chaque temps, glitch réduit aux
// deux temps qui suivent un changement de scène.
// SCÈNE (R20, 32 s) : la pochette nette (scène 0 + I-frame) dans la première
// seconde de chaque scène.
// CONFORT (R10) : luminance mesurée toutes les 0,5 s (le buffer réduit à
// 16 x 16), gain par VdjComfort nourri de mesures fraîches seulement.
//
// Debug : ?moshiframe=0 (jamais d'I-frame) | 1 (I-frame à chaque image : la
// source propre) ; ?moshvectors=1 (le champ de mouvement en couleur) ;
// ?moshscene=<n> force la scène (0 pochette, 1 grille, 2 néon, 3 titre,
// 4 tunnel).

const BLOCK = 16;
const MAXV = 48;                    // vecteurs codés sur +-48 px
const SCENE_S = 32;
const TIER = {
  0: { w: 480, h: 270, quant: 0 },
  1: { w: 640, h: 360, quant: 1 },
  2: { w: 640, h: 360, quant: 1 }
};
const FAMILIES = [[0, 2, 1, 4, 3], [0, 4, 2, 3, 1], [0, 1, 3, 2, 4], [0, 3, 4, 1, 2]];
const HUE_SHIFTS = [0, 0.33, 0.67, 0.5];

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
function hash1(n) { const x = Math.sin(n * 12.9898) * 43758.5453; return x - Math.floor(x); }

// le titre en capitales géantes, une fois par morceau
export function titleCaps(title) {
  const s = (title || '').toUpperCase().replace(/[^A-Z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();
  return s.length >= 2 ? s.slice(0, 12).trim() : '';
}
function titleTexture(text) {
  if (typeof document === 'undefined') return null;
  const W = 1024, H = 256;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const g = c.getContext('2d');
  if (!g) return null;
  g.fillStyle = '#000'; g.fillRect(0, 0, W, H);
  g.fillStyle = '#fff';
  let size = 230;
  g.font = `900 ${size}px "Helvetica Neue", Helvetica, Arial, sans-serif`;
  while (size > 60 && g.measureText(text).width > W - 40) { size -= 10; g.font = `900 ${size}px "Helvetica Neue", Helvetica, Arial, sans-serif`; }
  g.textAlign = 'center'; g.textBaseline = 'middle';
  g.fillText(text, W / 2, H / 2 + size * 0.04);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.NoColorSpace;
  t.minFilter = THREE.LinearFilter; t.magFilter = THREE.LinearFilter;
  t.wrapS = THREE.RepeatWrapping; t.wrapT = THREE.ClampToEdgeWrapping;
  t.generateMipmaps = false;
  t.needsUpdate = true;
  return t;
}

const QUAD_VERTEX = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;

// --- la source : cinq scènes --------------------------------------------------
const SOURCE_FRAGMENT = `
  precision highp float;
  uniform sampler2D uCover;
  uniform sampler2D uTitle;
  uniform float uHasCover;
  uniform float uCoverAspect;
  uniform float uScene;
  uniform float uBeat;           // temps continus depuis le début de la scène
  uniform vec4 uFlow;            // zoom (ln/temps), rotation (rad/temps), défilement (uv/temps)
  uniform vec3 uPalA;
  uniform vec3 uPalB;
  uniform vec3 uPalC;
  uniform vec2 uRes;
  uniform float uSeed;
  varying vec2 vUv;
  const float A = 16.0 / 9.0;
  float h21(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  vec3 coverAt(vec2 p) {
    // p en unités du cadre (x +-A/2, y +-0.5) ; la pochette COUVRE le cadre
    // (échelle max : son côté vaut A), lecture en miroir au-delà
    if (uHasCover < 0.5) {
      float d = length(p) * 2.0;
      vec3 c = mix(uPalA, uPalB, smoothstep(0.2, 0.9, d));
      return mix(c, uPalC, smoothstep(0.02, 0.0, abs(fract(d * 3.0 + uBeat * 0.25) - 0.5) - 0.42));
    }
    vec2 cuv = p / A * vec2(1.0, uCoverAspect) + 0.5;
    return texture2D(uCover, cuv).rgb;
  }
  vec3 polyNeon(vec2 p, float n, float r, float rot, vec3 col, float w) {
    float a = atan(p.y, p.x) + rot;
    float sec = 6.2832 / n;
    float d = length(p) * cos(mod(a, sec) - sec * 0.5) - r;      // distance signée au polygone
    float px = abs(d) * uRes.y;                                 // en pixels source
    float core = 1.0 - smoothstep(0.0, w, px);
    float glow = exp(-px / (w * 4.0)) * 0.35;
    return col * (glow + core * 0.9) + vec3(1.0) * core * 0.4;
  }
  void main() {
    vec2 p = (vUv - 0.5) * vec2(A, 1.0);
    vec3 c = vec3(0.0);
    float z = exp(uFlow.x * uBeat), rot = uFlow.y * uBeat;
    vec2 scroll = uFlow.zw * uBeat;
    if (uScene < 0.5) {
      // la pochette plein cadre, qui zoome (et tourne très peu)
      float cs = cos(rot), sn = sin(rot);
      vec2 q = mat2(cs, -sn, sn, cs) * p / z;
      c = coverAt(q);
    } else if (uScene < 1.5) {
      // la pochette en grille (3 de haut), qui défile en diagonale
      vec2 q = (p + scroll) * 3.0;
      vec2 cuv = q * vec2(1.0, uCoverAspect) + 0.5;
      c = uHasCover > 0.5 ? texture2D(uCover, cuv).rgb : coverAt(fract(q) - 0.5);
      // un joint sombre entre les tuiles
      vec2 f = abs(fract(q) - 0.5);
      c *= smoothstep(0.5, 0.47, max(f.x, f.y));
    } else if (uScene < 2.5) {
      // des formes néon qui tournent sur du noir (R1 dans la source)
      c += polyNeon(p, 3.0, 0.30, rot * 1.0 + uSeed, uPalA, 2.0);
      c += polyNeon(p, 6.0, 0.42, -rot * 0.7 + uSeed * 2.0, uPalB, 2.0);
      c += polyNeon(p - vec2(0.25 * sin(uBeat * 0.5), 0.0), 4.0, 0.16, rot * 1.6, uPalC, 1.6);
      c += polyNeon(p, 32.0, 0.47 + 0.02 * sin(uBeat * 3.14), rot * 0.3, mix(uPalA, uPalC, 0.5), 1.4);
    } else if (uScene < 3.5) {
      // le titre en capitales géantes, défilant, sur des bandes de palette
      float band = floor((vUv.y + scroll.y * 0.3) * 6.0);
      vec3 bg = mix(mix(uPalA, uPalB, step(1.5, mod(band, 3.0))), uPalC, step(2.5, mod(band, 3.0))) * 0.35;
      vec2 tuv = vec2(vUv.x * A / 4.0 + scroll.x * 0.5 + 0.5, (vUv.y - 0.5) * (A * 0.25 * 4.0) * 0.5 + 0.5);
      float t = texture2D(uTitle, tuv).r;
      c = mix(bg, mix(uPalC, vec3(1.0), 0.45) * 0.78, t);     // les lettres sous le seuil de bloom
    } else {
      // le tunnel : des anneaux de couleur qui foncent vers nous
      float r = length(p) + 0.02, a = atan(p.y, p.x);
      float k = log(r) * 3.0 - uBeat * 1.5 + 0.5 * sin(a * 3.0 + rot);
      float band = mod(floor(k), 3.0);
      vec3 col = band < 0.5 ? uPalA : (band < 1.5 ? uPalB : uPalC);
      float edge = smoothstep(0.0, 0.08, abs(fract(k) - 0.5) - 0.42);
      c = col * mix(1.0, 0.35, edge) * (0.35 + 0.45 * smoothstep(0.0, 0.5, r));
      c += vec3(1.0) * (1.0 - edge) * 0.18;
    }
    gl_FragColor = vec4(c, 1.0);
  }
`;

// --- le champ de mouvement : un texel par bloc, +-MAXV px codés sur 0..1 ----------
const MOTION_FRAGMENT = `
  precision highp float;
  uniform vec2 uBlocks;          // 40, 23
  uniform vec2 uRes;             // source px
  uniform vec4 uFlow;            // zoom, rotation, défilement (par temps)
  uniform float uBeatDt;         // temps écoulés cette image
  uniform float uKick;           // 0..1
  uniform float uKickSeed;
  uniform float uSwirl;          // 0..1
  uniform float uExplode;        // 0..1
  uniform float uGlitch;         // 0..1 : part des blocs qui glitchent
  uniform float uBeatIdx;
  varying vec2 vUv;
  float h21(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  void main() {
    vec2 b = floor(vUv * uBlocks);
    vec2 c = (b + 0.5) * ${BLOCK.toFixed(1)};             // centre du bloc, px source
    vec2 C = uRes * 0.5;
    vec2 d = c - C;
    // le mouvement connu de la scène, en px par image : zoom, rotation, défilement
    vec2 v = d * (exp(uFlow.x * uBeatDt) - 1.0) + vec2(-d.y, d.x) * (uFlow.y * uBeatDt) - uFlow.zw * uBeatDt * uRes.y;
    // le kick : des rangées de blocs qui glissent horizontalement
    float hr = h21(vec2(b.y, uKickSeed));
    if (hr < 0.35 * uKick) v.x += (h21(vec2(b.y * 3.1, uKickSeed + 7.0)) - 0.5) * 2.0 * 28.0 * uKick;
    // les aigus : un tourbillon
    float len = max(length(d), 1.0);
    v += vec2(-d.y, d.x) / len * uSwirl * 6.0 * (0.5 + 0.5 * h21(b + uBeatIdx));
    // le drop : les blocs explosent vers l'extérieur
    v += d * 0.09 * uExplode * (0.6 + 0.8 * h21(b * 1.7 + uBeatIdx));
    // les modes de glitch du bloc, tirés par temps : gel, posterisation, séparation RGB
    float g = h21(b + floor(uBeatIdx) * 0.731);
    float mode = g < uGlitch * 0.12 ? 0.25 : (g < uGlitch * 0.30 ? 0.5 : (g < uGlitch * 0.55 ? 0.75 : 0.0));
    gl_FragColor = vec4(clamp(v / ${MAXV.toFixed(1)} * 0.5 + 0.5, 0.0, 1.0), mode, 1.0);
  }
`;

// --- la P-frame ----------------------------------------------------------------
const MOSH_FRAGMENT = `
  precision highp float;
  uniform sampler2D uPrev;
  uniform sampler2D uSrc;
  uniform sampler2D uStale;
  uniform sampler2D uMotion;
  uniform vec2 uRes;
  uniform vec2 uBlocks;
  uniform float uIFrame;         // 1 : l'image propre
  uniform float uBright;         // l'I-frame éclatante du drop
  uniform float uTear;           // 0..1
  uniform float uTearSeed;
  uniform float uRgb;            // px
  uniform float uQuant;          // 0 / 1
  uniform float uGlitch;
  varying vec2 vUv;
  float h21(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  void main() {
    vec3 src = texture2D(uSrc, vUv).rgb;
    if (uIFrame > 0.999) { gl_FragColor = vec4(src * uBright, 1.0); return; }
    // le bloc et son vecteur (lecture au plus proche : un vecteur par bloc)
    vec2 b = floor(vUv * uBlocks);
    vec4 m = texture2D(uMotion, (b + 0.5) / uBlocks);
    vec2 v = (m.rg * 2.0 - 1.0) * ${MAXV.toFixed(1)};
    float mode = m.b;
    vec2 uv = vUv;
    // les déchirures : des bandes de 6 à 20 px décalées horizontalement
    float band = floor(vUv.y * uRes.y / 12.0);
    float ht = h21(vec2(band, uTearSeed));
    if (ht < uTear * 0.25) uv.x += (h21(vec2(band * 1.3, uTearSeed + 3.0)) - 0.5) * 80.0 / uRes.x * uTear;
    vec2 puv = uv - v / uRes;                              // d'où vient ce pixel
    vec3 col;
    if (mode > 0.2 && mode < 0.3) {
      col = texture2D(uStale, vUv).rgb;                    // le bloc figé : l'image d'il y a 4 images
    } else if (mode > 0.7) {
      // séparation RGB par bloc
      vec2 dr = vec2(uRgb, 0.0) / uRes;
      col = vec3(texture2D(uPrev, puv + dr).r, texture2D(uPrev, puv).g, texture2D(uPrev, puv - dr).b);
    } else {
      col = texture2D(uPrev, puv).rgb;
    }
    if (mode > 0.45 && mode < 0.55 && uQuant > 0.5) col = floor(col * 4.0 + 0.5) / 4.0;   // DCT simulée : 4 niveaux
    col = mix(col, src * uBright, uIFrame);
    gl_FragColor = vec4(col, 1.0);
  }
`;

const BLIT_FRAGMENT = `
  precision mediump float;
  uniform sampler2D uTex;
  varying vec2 vUv;
  void main() { gl_FragColor = texture2D(uTex, vUv); }
`;

// --- l'affichage : cover-fit (R18), gain, masque de la pochette ----------------
const DISPLAY_FRAGMENT = `
  precision mediump float;
  uniform sampler2D uTex;
  uniform sampler2D uMotion;
  uniform float uGain;
  uniform float uZoom;
  uniform float uShowVectors;
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;
  const float A = 16.0 / 9.0;
  void main() {
    // la source 16:9 COUVRE l'écran : échelle max, recadrage centré, à-coup de zoom
    vec2 s = vUv - 0.5;
    if (uAspect > A) s.y *= A / uAspect; else s.x *= uAspect / A;
    s /= uZoom;
    vec2 uv = s + 0.5;
    vec3 col = texture2D(uTex, uv).rgb * uGain;
    if (uShowVectors > 0.5) { vec4 m = texture2D(uMotion, uv); col = mix(col, vec3(m.r, m.g, m.b), 0.6); }
    vec2 sc = vec2((vUv.x * 2.0 - 1.0) * uAspect, vUv.y * 2.0 - 1.0);
    vec2 q = abs(sc - uSafeCenter) - uSafeHalfSize;
    float sd = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    col *= mix(0.45, 1.0, smoothstep(0.0, uSafeFeather, sd));
    gl_FragColor = vec4(col, 1.0);
  }
`;

export class DatamoshVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    this._layout = TIER[this._tier] || TIER[1];
    const fi = urlParam('moshiframe');
    this._forceIFrame = fi === '0' ? 0 : fi === '1' ? 1 : null;
    this._showVectors = urlParam('moshvectors') === '1';
    const fs = parseInt(urlParam('moshscene'), 10);
    this._forceScene = fs >= 0 && fs <= 4 ? fs : null;
    this._post = new VdjPost({ bloomStrength: 0.35, bloomThreshold: 0.78, grain: 0.02, vignette: 0.3, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 });
    this._comfort = new VdjComfort(VDJ_PRESETS.party.comfort);
  }

  get postProcessing() { return this._post.value; }

  init(scene) {
    this._scene = scene;
    const L = this._layout;
    this._adapt = new Adapt();
    this._clock = new SceneClock({ period: SCENE_S, start: 0 });
    this._ortho = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 1);
    this._blocks = new THREE.Vector2(Math.ceil(L.w / BLOCK), Math.ceil(L.h / BLOCK));
    this._palA = new THREE.Color(0.9, 0.1, 0.5); this._palB = new THREE.Color(0.1, 0.8, 0.9); this._palC = new THREE.Color(1.0, 0.8, 0.1);
    this._titleTex = titleTexture('HALO') || new THREE.DataTexture(new Uint8Array([0, 0, 0, 255]), 1, 1, THREE.RGBAFormat);
    this._titleTex.needsUpdate = true;
    this._title = '';
    this._coverUrl = undefined; this._coverTex = null;

    const mk = (vs, fs, uniforms) => {
      const m = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms, vertexShader: vs, fragmentShader: fs, depthTest: false, depthWrite: false }));
      m.frustumCulled = false;
      const sc = new THREE.Scene(); sc.add(m);
      return { scene: sc, mesh: m, u: uniforms };
    };
    this._src = mk(QUAD_VERTEX, SOURCE_FRAGMENT, {
      uCover: { value: null }, uTitle: { value: this._titleTex }, uHasCover: { value: 0 }, uCoverAspect: { value: 1 },
      uScene: { value: 0 }, uBeat: { value: 0 }, uFlow: { value: new THREE.Vector4(0.08, 0, 0, 0) },
      uPalA: { value: this._palA }, uPalB: { value: this._palB }, uPalC: { value: this._palC },
      uRes: { value: new THREE.Vector2(L.w, L.h) }, uSeed: { value: 0 }
    });
    this._mot = mk(QUAD_VERTEX, MOTION_FRAGMENT, {
      uBlocks: { value: this._blocks }, uRes: { value: new THREE.Vector2(L.w, L.h) }, uFlow: this._src.u.uFlow,
      uBeatDt: { value: 0 }, uKick: { value: 0 }, uKickSeed: { value: 0 }, uSwirl: { value: 0 }, uExplode: { value: 0 },
      uGlitch: { value: 0 }, uBeatIdx: { value: 0 }
    });
    this._mosh = mk(QUAD_VERTEX, MOSH_FRAGMENT, {
      uPrev: { value: null }, uSrc: { value: null }, uStale: { value: null }, uMotion: { value: null },
      uRes: { value: new THREE.Vector2(L.w, L.h) }, uBlocks: { value: this._blocks },
      uIFrame: { value: 1 }, uBright: { value: 1 }, uTear: { value: 0 }, uTearSeed: { value: 0 }, uRgb: { value: 0 },
      uQuant: { value: L.quant }, uGlitch: { value: 0 }
    });
    this._blit = mk(QUAD_VERTEX, BLIT_FRAGMENT, { uTex: { value: null } });
    this._du = {
      uTex: { value: null }, uMotion: { value: null }, uGain: { value: 1 }, uZoom: { value: 1 }, uShowVectors: { value: this._showVectors ? 1 : 0 },
      uAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter,
      uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
    };
    this._quad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._du, vertexShader: QUAD_VERTEX, fragmentShader: DISPLAY_FRAGMENT, depthTest: false, depthWrite: false }));
    this._quad.frustumCulled = false; this._quad.renderOrder = -3000;
    const self = this;
    this._quad.onBeforeRender = function (renderer) { self._render(renderer); };
    scene.add(this._quad);
    this._measRT = new THREE.WebGLRenderTarget(16, 16, { type: THREE.UnsignedByteType, depthBuffer: false, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter });
    this._measBuf = new Uint8Array(16 * 16 * 4);

    this._time = 0; this._beats = 0; this._bars = 0; this._phraseN = 0; this._beatAge = 9; this._barAge = 9;
    this._sceneBeat0 = 0; this._sceneType = 0; this._family = 0; this._palette = 0; this._paletteRef = undefined;
    this._build = 0; this._dropAge = 99; this._explode = 0; this._kick = 0; this._kickSeed = 0; this._swirl = 0; this._tearSeed = 0;
    this._iframe = 1; this._boost = 0; this._pendingI = true; this._frame = 0; this._staleAge = 0;
    this._lumAge = 0; this._lumMeasured = 0.3; this._lumFresh = false; this._gain = 1; this._paused = false; this._dirty = false;
    this._applyPalette(null);
    this._pickScene(0, true);
  }

  _applyPalette(pal) {
    const v = pal && pal.vividColors && pal.vividColors.length ? pal.vividColors : [0xff2a7a, 0x22ccee, 0xffcc22];
    const sh = HUE_SHIFTS[this._palette % HUE_SHIFTS.length];
    const a = vdjColor(v[0], new THREE.Color(), VDJ_PRESETS.party.saturation); a.offsetHSL(sh, 0, 0);
    const b = vdjColor(v[1 % v.length], new THREE.Color(), VDJ_PRESETS.party.saturation); b.offsetHSL(sh + 0.38, 0, 0);
    const c = vdjColor(v[2 % v.length], new THREE.Color(), VDJ_PRESETS.party.saturation); c.offsetHSL(sh + 0.71, 0, 0);
    this._palA.copy(a); this._palB.copy(b); this._palC.copy(c);
  }

  _loadCover(profile) {
    if (profile.artworkUrl === this._coverUrl) return;
    this._coverUrl = profile.artworkUrl;
    if (this._coverTex) { this._coverTex.dispose(); this._coverTex = null; }
    this._src.u.uHasCover.value = 0;
    if (!profile.artworkUrl) return;
    const self = this, url = profile.artworkUrl;
    loadArtworkTexture(url, { wrap: THREE.MirroredRepeatWrapping, mipmaps: true }, (tex) => {
      if (!self._scene || url !== self._coverUrl) { tex.dispose(); return; }
      self._coverTex = tex; self._src.u.uCover.value = tex; self._src.u.uHasCover.value = 1;
      const img = tex.image;
      self._src.u.uCoverAspect.value = img && img.width ? img.width / img.height : 1;
      self._pendingI = true;
    }, () => {});
  }

  onTrackChange(profile) {
    this._loadCover(profile);
    const t = titleCaps(profile && profile.title);
    if (t !== this._title) {
      this._title = t;
      const tex = titleTexture(t || 'HALO');
      if (tex) { if (this._titleTex) this._titleTex.dispose(); this._titleTex = tex; this._src.u.uTitle.value = tex; }
    }
  }

  // la scène de la mesure : un type (famille de la phrase), ses vecteurs connus
  _pickScene(bar, coverFirst) {
    const fam = FAMILIES[this._family % FAMILIES.length];
    let type = coverFirst ? 0 : fam[bar % fam.length];
    if (this._forceScene != null) type = this._forceScene;
    this._sceneType = type;
    const r = (k) => hash1(bar * 7.13 + k + this._family * 31);
    const f = this._src.u.uFlow.value;
    const sgn = (x) => (x < 0.5 ? -1 : 1);
    if (type === 0) f.set(0.10 + 0.10 * r(1), (r(2) - 0.5) * 0.06, 0, 0);                 // zoom in, léger roulis
    else if (type === 1) f.set(0, 0, 0.12 * sgn(r(3)), 0.07 * sgn(r(4)));                  // défilement diagonal
    else if (type === 2) f.set(0, 0.9 + 0.8 * r(5), 0, 0);                                 // rotation
    else if (type === 3) f.set(0, 0, 0.16 * sgn(r(6)), 0.02);                              // le titre défile
    else f.set(-0.06, 0.2 * (r(7) - 0.5), 0, 0);                                           // le tunnel fonce vers nous
    this._src.u.uSeed.value = r(8) * 6.28;
    this._sceneBeat0 = this._beatCont || 0;
  }

  update(deltaTime, profile) {
    const paused = profile.playbackState !== 'PLAYING';
    this._paused = paused;
    const dt = paused ? 0 : Math.min(deltaTime, 0.05);
    const a = this._adapt.update(dt, profile);
    if (profile.artworkPalette !== this._paletteRef) { this._paletteRef = profile.artworkPalette; this._applyPalette(profile.artworkPalette); }
    if (profile.artworkUrl !== this._coverUrl) this._loadCover(profile);
    if (this._title === '' && profile.title) this.onTrackChange(profile);
    if (paused) return;                                  // l'image tient : aucune P-frame
    this._time += dt;
    this._clock.update(dt);
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 128;
    const beatS = 60 / bpm, barS = 4 * beatS;
    const director = profile.director || null;

    // la montée (un drop qui approche, ou le segment « build »)
    const next = director && director.nextEvent;
    let build = 0;
    if (next && next.type === 'drop' && next.msUntil < 8000) build = 1 - next.msUntil / 8000;
    if (director && director.mode === 'build') build = Math.max(build, 0.6);
    this._build += (build - this._build) * (1 - Math.exp(-dt / 1.0));

    // les horloges
    if (a.beat) { this._beats++; this._beatAge = 0; }
    this._beatAge += dt;
    const bp = a.beatPhase != null ? a.beatPhase : clamp01(this._beatAge / beatS);
    const prevCont = this._beatCont || 0;
    this._beatCont = this._beats + bp;
    const beatDt = Math.max(0, this._beatCont - prevCont);
    const beatInBar = ((this._beats % 4) + 4) % 4;
    const low = a.bands ? a.bands.low : 0.5 + 0.5 * a.pulse * (bp < 0.1 ? 1 : 0);
    const high = a.bands ? a.bands.high : 0.3 * a.intensity;
    const calm = a.intensity < 0.6;

    let iframe = 0;
    if (a.bar) {
      this._bars++; this._barAge = 0;
      // la mesure : nouvelle scène SANS I-frame (le datamosh entre les deux)
      this._pickScene(this._bars, false);
    }
    this._barAge += dt;
    if (a.phrase) { this._phraseN++; this._family++; this._palette++; this._applyPalette(profile.artworkPalette); }
    if (this._clock.wrapped || this._pendingI) {
      // la scène (R20) : la pochette nette dans la première seconde
      if (this._clock.wrapped) this._pickScene(this._bars, true);
      iframe = 1; this._pendingI = false;
    }
    if (a.beat) {
      // I-frame sur les temps 1 et 3, jamais sur le temps 1 d'une mesure
      // (sa scène vient de changer) ; sous 0,6 d'intensité, à chaque temps
      const want = calm ? beatInBar !== 0 : (beatInBar === 2 || (beatInBar === 0 && !a.bar));
      // la montée : les I-frames se raréfient, l'image fond
      if (want && hash1(this._beats * 3.3) >= this._build * 0.95) iframe = 1;
      // le kick : des rangées de blocs glissent
      this._kick = 1; this._kickSeed = this._beats;
      this._tearSeed = this._beats * 1.7;
    }
    this._kick *= Math.exp(-dt / 0.09);
    this._swirl += (clamp01(high) - this._swirl) * (1 - Math.exp(-dt / 0.15));
    // le drop : I-frame éclatante, flash, puis une mesure d'explosion
    // l'I-frame éclatante : un surcroît de gain à l'AFFICHAGE qui retombe en
    // un temps - cuit dans le buffer (x1,35), il restait toute la mesure et
    // l'explosion étirait du blanc sur tout l'écran
    const bright = 1;
    if (a.drop) {
      this._dropAge = 0; iframe = 1; this._boost = 0.3;
      if (director && this._comfort.requestFlash(this._time)) director.flash = VDJ_DROP_FLASH;
    }
    this._dropAge += dt;
    this._explode = this._dropAge < barS ? Math.pow(1 - this._dropAge / barS, 2) : 0;
    this._boost = (this._boost || 0) * Math.exp(-dt / (beatS * 0.4));
    if (this._forceIFrame != null) iframe = this._forceIFrame;

    // le glitch : plein au-dessus de 0,6, réduit aux deux temps qui suivent un
    // changement de scène en dessous
    const sinceScene = this._beatCont - this._sceneBeat0;
    const transition = sinceScene < 2 ? 1 : 0;
    const glitch = calm ? 0.35 * transition : 0.4 + 0.6 * clamp01((a.intensity - 0.6) / 0.4);

    const su = this._src.u, mu = this._mot.u, pu = this._mosh.u;
    su.uScene.value = this._sceneType;
    su.uBeat.value = sinceScene;
    mu.uBeatDt.value = beatDt;
    mu.uKick.value = this._kick * (0.4 + 0.6 * clamp01(low)) * (calm ? transition : 1);
    mu.uKickSeed.value = this._kickSeed;
    mu.uSwirl.value = this._swirl * (calm ? 0.3 : 1);
    mu.uExplode.value = this._explode;
    mu.uGlitch.value = glitch;
    mu.uBeatIdx.value = this._beats;
    pu.uIFrame.value = iframe; pu.uBright.value = bright;
    pu.uTear.value = glitch * (0.5 + 0.5 * this._kick);
    pu.uTearSeed.value = this._tearSeed;
    pu.uRgb.value = 3 * glitch;
    pu.uGlitch.value = glitch;
    this._dirty = true;

    this._du.uZoom.value = vdjCameraKick(a.barPhase != null ? a.barPhase : clamp01(this._barAge / barS), this._bars, 4).zoom;
    this._lumAge += dt;
    if (this._lumFresh) { this._lumFresh = false; this._gain = this._comfort.measure(this._lumMeasured * this._gain, 0.5); }
    this._du.uGain.value = this._gain * (1 + (this._boost || 0));
    this._post.update(vdjBeatChroma(bp, beatS));
    this._lastI = iframe; this._glitch = glitch;
  }

  _ensureTargets(renderer) {
    if (this._rt) return;
    const L = this._layout;
    const ext = renderer.extensions;
    const floatOk = renderer.capabilities.isWebGL2 && (ext.has('EXT_color_buffer_float') || ext.has('EXT_color_buffer_half_float'));
    const colour = () => new THREE.WebGLRenderTarget(L.w, L.h, { type: floatOk ? THREE.HalfFloatType : THREE.UnsignedByteType, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, depthBuffer: false, wrapS: THREE.ClampToEdgeWrapping, wrapT: THREE.ClampToEdgeWrapping, generateMipmaps: false });
    // le Pi ne filtre pas le flottant : la lecture bilinéaire dans le bloc
    // demande des octets là-bas ; en demi-flottant seulement si filtrable
    const canFilterFloat = floatOk && ext.has('OES_texture_float_linear');
    const mkColour = canFilterFloat ? colour : () => new THREE.WebGLRenderTarget(L.w, L.h, { type: THREE.UnsignedByteType, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, depthBuffer: false, generateMipmaps: false });
    this._rt = { src: mkColour(), a: mkColour(), b: mkColour(), stale: mkColour() };
    this._motRT = new THREE.WebGLRenderTarget(this._blocks.x, this._blocks.y, { type: THREE.UnsignedByteType, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, depthBuffer: false, generateMipmaps: false });
    this._floatOk = canFilterFloat;
  }

  _render(renderer) {
    this._ensureTargets(renderer);
    const prev = renderer.getRenderTarget();
    renderer.getClearColor(_cc); const ca = renderer.getClearAlpha();
    const autoClear = renderer.autoClear;
    renderer.autoClear = false;
    if (this._dirty) {
      this._dirty = false;
      // 1. la source propre
      renderer.setRenderTarget(this._rt.src);
      renderer.render(this._src.scene, this._ortho);
      // 2. le champ de mouvement
      renderer.setRenderTarget(this._motRT);
      renderer.render(this._mot.scene, this._ortho);
      // 3. la P-frame (ou l'I-frame)
      const pu = this._mosh.u;
      pu.uPrev.value = this._rt.a.texture; pu.uSrc.value = this._rt.src.texture; pu.uStale.value = this._rt.stale.texture; pu.uMotion.value = this._motRT.texture;
      renderer.setRenderTarget(this._rt.b);
      renderer.render(this._mosh.scene, this._ortho);
      const t = this._rt.a; this._rt.a = this._rt.b; this._rt.b = t;
      // 4. l'image d'il y a 4 images, pour les blocs figés
      this._staleAge++;
      if (this._staleAge >= 4) {
        this._staleAge = 0;
        this._blit.u.uTex.value = this._rt.a.texture;
        renderer.setRenderTarget(this._rt.stale);
        renderer.render(this._blit.scene, this._ortho);
      }
      // 5. la mesure du confort, toutes les 0,5 s : le buffer réduit à 16 x 16
      if (this._lumAge >= 0.5) {
        this._lumAge = 0;
        this._blit.u.uTex.value = this._rt.a.texture;
        renderer.setRenderTarget(this._measRT);
        renderer.render(this._blit.scene, this._ortho);
        try {
          renderer.readRenderTargetPixels(this._measRT, 0, 0, 16, 16, this._measBuf);
          let sum = 0;
          for (let i = 0; i < this._measBuf.length; i += 4) sum += 0.2126 * this._measBuf[i] + 0.7152 * this._measBuf[i + 1] + 0.0722 * this._measBuf[i + 2];
          this._lumMeasured = sum / (16 * 16 * 255);
          this._lumFresh = true;
        } catch (e) { /* la prochaine fois */ }
      }
    }
    this._du.uTex.value = this._rt.a.texture;
    this._du.uMotion.value = this._motRT.texture;
    renderer.setRenderTarget(prev);
    renderer.setClearColor(_cc, ca);
    renderer.autoClear = autoClear;
  }

  getProbe() {
    return {
      t: +this._time.toFixed(2), beats: this._beats, bars: this._bars, scene: this._sceneType, family: this._family % FAMILIES.length,
      iframe: this._lastI || 0, glitch: +(this._glitch || 0).toFixed(3), build: +this._build.toFixed(3), explode: +this._explode.toFixed(3),
      lum: +this._lumMeasured.toFixed(3), gain: +this._gain.toFixed(3), cover: this._src.u.uHasCover.value, title: this._title, floatOk: !!this._floatOk
    };
  }

  dispose() {
    if (this._quad) { this._quad.onBeforeRender = null; if (this._scene) this._scene.remove(this._quad); this._quad.geometry.dispose(); this._quad.material.dispose(); }
    for (const p of [this._src, this._mot, this._mosh, this._blit]) if (p) { p.mesh.geometry.dispose(); p.mesh.material.dispose(); }
    if (this._rt) for (const k in this._rt) this._rt[k].dispose();
    if (this._motRT) this._motRT.dispose();
    if (this._measRT) this._measRT.dispose();
    if (this._titleTex) this._titleTex.dispose();
    if (this._coverTex) this._coverTex.dispose();
    if (this._adapt) this._adapt.dispose();
    this._scene = null;
  }
}

const _cc = new THREE.Color();
